// GeoVinumMap — embeds the interactive EtOH/geoVINUM appellation map.
//
// We never expose the EtOH API key to the browser. Instead we hit our own
// backend route `/api/geovinum-token` which trades the long-lived key for a
// short-lived token (cached server-side 55 min).
//
// Failure modes are all silent: if the token request fails or the appellation
// is empty, we render nothing — the article must keep working.
//
// Designed to be `React.lazy()`-loaded by the parent so the iframe never
// blocks the article's main paint.
import { useEffect, useRef, useState } from "react";
import { apiFetch } from "@/lib/api";

const IFRAME_BASE = "https://iframe.geovinum.app/";

const GeoVinumMap = ({ appellation, onUnavailable }) => {
  const [token, setToken] = useState(null);
  const [failed, setFailed] = useState(false);
  const containerRef = useRef(null);
  const [inView, setInView] = useState(false);

  // Notify the parent so it can hide the whole aside (no orphan caption /
  // empty 650px block) when the map can't load.
  useEffect(() => {
    if (failed && onUnavailable) onUnavailable();
  }, [failed, onUnavailable]);

  // If the appellation is empty, signal "no map" once and bail.
  useEffect(() => {
    if (!appellation && onUnavailable) onUnavailable();
  }, [appellation, onUnavailable]);

  // Defer the iframe network call until the container scrolls into view —
  // articles loaded from search engines often never reach the map.
  useEffect(() => {
    if (!containerRef.current) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return undefined;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setInView(true);
            obs.disconnect();
            break;
          }
        }
      },
      { rootMargin: "200px 0px" },
    );
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!inView) return undefined;
    let cancelled = false;
    // Safety net: if the token call hangs for any reason (slow network, EtOH
    // outage, etc.) we surface a failure instead of a perpetual spinner.
    const safety = setTimeout(() => { if (!cancelled) setFailed(true); }, 12000);
    (async () => {
      const { ok, data: d } = await apiFetch(`/api/geovinum-token`);
      if (cancelled) return;
      if (!ok || !d?.token) {
        setFailed(true);
      } else {
        setToken(d.token);
      }
    })();
    return () => { cancelled = true; clearTimeout(safety); };
  }, [inView]);

  // Silent failure modes — never break the page.
  if (!appellation) return null;
  if (failed) return null;

  const ready = Boolean(token);
  const src = ready
    ? `${IFRAME_BASE}?token=${encodeURIComponent(token)}&app=${encodeURIComponent(appellation)}`
    : "";

  return (
    <div
      ref={containerRef}
      className="w-full rounded-[12px] overflow-hidden bg-[color:var(--gv-mist)] border border-[color:var(--gv-line)]"
      style={{ height: "650px" }}
      data-testid="geovinum-map"
      data-appellation={appellation}
    >
      {ready ? (
        <iframe
          src={src}
          title={`Carte geoVINUM - ${appellation}`}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allow="geolocation; fullscreen"
          className="w-full h-full"
          style={{ border: 0, display: "block" }}
          data-testid="geovinum-map-iframe"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-[color:var(--gv-ink)]/40 text-[12px]" data-testid="geovinum-map-loading">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[color:var(--gv-pink)] animate-pulse mr-2" />
          Chargement de la carte {appellation}…
        </div>
      )}
    </div>
  );
};

export default GeoVinumMap;
